import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider } from "@/contexts/AppContext";
import { UserAuthProvider } from "@/contexts/UserAuthContext";
import { UserAuthGuard } from "@/components/layout/UserAuthGuard";
import { AppLayout } from "@/components/layout/AppLayout";
import { useStatusBar } from "@/hooks/useStatusBar";
import { useSplashScreen } from "@/hooks/useSplashScreen";
import { isAdminEnabled } from "@/config/env";
import Home from "./pages/Home";
import CouponDetail from "./pages/CouponDetail";
import Favorites from "./pages/Favorites";
import More from "./pages/More";
import Account from "./pages/Account";
import CashbackHistory from "./pages/CashbackHistory";
import CashbackWalletManager from "./pages/CashbackWalletManager";
import ReferralPage from "./pages/ReferralPage";
import NotFound from "./pages/NotFound";
import Onboarding from "./pages/Onboarding";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import SignUpReferralCode from "./pages/SignUpReferralCode";
import VerifyEmail from "./pages/VerifyEmail";
import EmailAction from "./pages/EmailAction";
import ForgotPassword from "./pages/ForgotPassword";
import { AndroidBackButtonHandler } from "./components/mobile/AndroidBackButtonHandler";
import { AnimatedSplash } from "./components/mobile/AnimatedSplash";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";

// Conditionally import admin components only if admin is enabled
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCountries from "./pages/admin/AdminCountries";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminStores from "./pages/admin/AdminStores";
import AdminStoreRequests from "./pages/admin/AdminStoreRequests";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminReports from "./pages/admin/AdminReports";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminStatistics from "./pages/admin/AdminStatistics";
import AdminPurchaseImport from "./pages/admin/AdminPurchaseImport";

// Component to redirect admin routes to home when admin is disabled
const AdminRedirect = () => <Navigate to="/" replace />;

const queryClient = new QueryClient();

const App = () => {
  // Initialize mobile-specific features
  useStatusBar();
  useSplashScreen();

  return (
    <QueryClientProvider client={queryClient}>
      <AdminAuthProvider>
        <UserAuthProvider>
          <AppProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <AnimatedSplash />
              <HashRouter>
                <AndroidBackButtonHandler />
                <UserAuthGuard>
                  <Routes>
                  {/* Admin Routes - Only available when VITE_ENABLE_ADMIN=true */}
                  {isAdminEnabled() ? (
                    <>
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route
                        path="/admin"
                        element={
                          <AdminProtectedRoute>
                            <AdminDashboard />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/countries"
                        element={
                          <AdminProtectedRoute>
                            <AdminCountries />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/categories"
                        element={
                          <AdminProtectedRoute>
                            <AdminCategories />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/stores"
                        element={
                          <AdminProtectedRoute>
                            <AdminStores />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/store-requests"
                        element={
                          <AdminProtectedRoute>
                            <AdminStoreRequests />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/coupons"
                        element={
                          <AdminProtectedRoute>
                            <AdminCoupons />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users/*"
                        element={
                          <AdminProtectedRoute>
                            <AdminUsers />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/leads"
                        element={
                          <AdminProtectedRoute>
                            <Navigate to="/admin/users" replace />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/reports"
                        element={
                          <AdminProtectedRoute>
                            <AdminReports />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/settings"
                        element={
                          <AdminProtectedRoute>
                            <AdminSettings />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/statistics"
                        element={
                          <AdminProtectedRoute>
                            <AdminStatistics />
                          </AdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/purchase-import"
                        element={
                          <AdminProtectedRoute>
                            <AdminPurchaseImport />
                          </AdminProtectedRoute>
                        }
                      />
                    </>
                  ) : (
                    <>
                      {/* Redirect all admin routes to home when admin is disabled */}
                      <Route path="/admin/*" element={<AdminRedirect />} />
                    </>
                  )}

                  {/* Auth Routes - Always accessible */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route
                    path="/signup/referral-code"
                    element={<SignUpReferralCode />}
                  />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/email-action" element={<EmailAction />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/onboarding" element={<Onboarding />} />

                  {/* User Routes */}
                  <Route
                    path="/"
                    element={
                      <AppLayout>
                        <Home />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/coupon/:id"
                    element={
                      <AppLayout>
                        <CouponDetail />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/favorites"
                    element={
                      <AppLayout>
                        <Favorites />
                      </AppLayout>
                    }
                  />
                  {/* Legacy /more → redirect to /account */}
                  <Route
                    path="/more"
                    element={<Navigate to="/account" replace />}
                  />
                  <Route
                    path="/account"
                    element={
                      <AppLayout>
                        <Account />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/cashback"
                    element={
                      <ErrorBoundary>
                        <AppLayout>
                          <CashbackHistory />
                        </AppLayout>
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/cashback/referral"
                    element={
                      <ErrorBoundary>
                        <AppLayout>
                          <ReferralPage />
                        </AppLayout>
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/cashback/wallet"
                    element={
                      <ErrorBoundary>
                        <AppLayout>
                          <CashbackWalletManager />
                        </AppLayout>
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/account/cashback"
                    element={<Navigate to="/cashback" replace />}
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </UserAuthGuard>
              </HashRouter>
            </TooltipProvider>
          </AppProvider>
        </UserAuthProvider>
      </AdminAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
